import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useLocation, useParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { auth } from '../../firebase';
import { pillarIdToTopicMap, getGrammarLessonUrl, getGrammarTopicTitle } from '../../services/grammarTopicsMap';
import {
  ArrowLeft,
  BookOpen,
  Brain,
  ChevronRight,
  HelpCircle,
  Trophy,
  Target,
  Clock,
  Zap,
  GraduationCap,
  AlertCircle,
  CheckCircle,
  Lock,
  Link2,
  PlayCircle,
  Home,
  Play
} from 'lucide-react';
import grammarProgressService from '../../services/grammarProgressService';
import GrammarPillarSelector from './GrammarPillarSelector';
import GrammarExerciseEngine from './GrammarExerciseEngine';
import FloatingProgressTracker from './FloatingProgressTracker';
import GrammarGuide from './GrammarGuide';
import GrammarBottomNav from './GrammarBottomNav';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import subscriptionService from '../../services/subscriptionService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const GrammarHub = ({ onBack, userLevel = 'B1' }) => {
  const { t, i18n } = useTranslation();
  const outletContext = useOutletContext();
  const { setFocusMode } = outletContext || {};
  const location = useLocation();
  const { examSlug } = useParams();
  const [currentView, setCurrentView] = useState('levels');
  // ?level=A2 opens another course level (the results page sends B1 students to A2 topics B1 doesn't cover).
  // Read once at mount so the user's own level is never loaded first and its pillars mixed up with the requested one.
  const [selectedLevel, setSelectedLevel] = useState(() => {
    const requested = new URLSearchParams(location.search).get('level')?.toUpperCase();
    return /^(A2|B1|B2|C1)$/.test(requested || '') ? requested : userLevel;
  });
  const [selectedPillar, setSelectedPillar] = useState(null);

  // Tab title: same as category/topic name when viewing a topic (e.g. "Konjunktiv II | Grammar | Logami"), else "Grammar | Logami"
  useEffect(() => {
    const topicTitle = currentView === 'exercises' && selectedLevel && selectedPillar
      ? getGrammarTopicTitle(selectedLevel, selectedPillar)
      : null;
    const prev = document.title;
    document.title = topicTitle ? `${topicTitle} | Grammar | Logami` : 'Grammar | Logami';
    return () => { document.title = prev; };
  }, [currentView, selectedLevel, selectedPillar]);

  // Clear focus mode when leaving grammar practice (e.g. browser back, navigate to lessons) so it never gets stuck
  useEffect(() => {
    return () => {
      if (setFocusMode) setFocusMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup only on unmount
  }, []);
  const [overallProgress, setOverallProgress] = useState(0);
  const [userProgress, setUserProgress] = useState(null);
  const [continuationPoint, setContinuationPoint] = useState(null);
  const [initialExerciseId, setInitialExerciseId] = useState(null);
  const [initialExerciseIndex, setInitialExerciseIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const hasAutoAdvanced = useRef(false);
  const navigate = useLocalizedNavigate();
  const [showGuide, setShowGuide] = useState(false);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);
  const [lessonData, setLessonData] = useState(null);
  const [practiceData, setPracticeData] = useState(null);
  const [isContinueHovered, setIsContinueHovered] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [pillarConnections, setPillarConnections] = useState({});
  const [currentContext, setCurrentContext] = useState({
    pillarId: null,
    categoryId: null,
    categoryName: null,
    practiceLevel: null,
    practiceLevelName: null,
    structureType: null
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const user = auth.currentUser;
  const processedPillarFromUrl = useRef(null);

  const grammarLevels = {
    'A2': {
      available: true,
      color: 'bg-black',
      textColor: 'text-black',
      bgColor: 'bg-white',
      borderColor: 'border-black'
    },
    'B1': {
      available: true,
      color: 'bg-black',
      textColor: 'text-black',
      bgColor: 'bg-white',
      borderColor: 'border-black'
    },
    'B2': {
      available: true,
      color: 'bg-black',
      textColor: 'text-black',
      bgColor: 'bg-white',
      borderColor: 'border-black'
    },
    'C1': {
      available: true,
      color: 'bg-black',
      textColor: 'text-black',
      bgColor: 'bg-white',
      borderColor: 'border-black'
    }
  };

  const loadLevelData = async (level) => {
    if (!user) return;

    console.log(`📄 LOADING LEVEL DATA: ${level}`);
    setLoading(true);
    try {
      const progress = await grammarProgressService.getUserProgress(user.uid, level);
      console.log(`📊 LOADED PROGRESS:`, progress);
      setUserProgress(progress);

      const overall = await grammarProgressService.getOverallProgress(user.uid, level);
      console.log(`📈 OVERALL PROGRESS: ${overall}%`);
      setOverallProgress(overall);

      const continuation = await grammarProgressService.getContinuationPoint(user.uid, level);
      console.log(`🔖 CONTINUATION POINT:`, continuation);
      setContinuationPoint(continuation);

      await loadLessonPracticeConnections(level);
    } catch (error) {
      console.error('❌ Error loading level data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check guide status on mount and when user changes
  useEffect(() => {
    const checkGuideStatus = async () => {
      // First check localStorage (works for all users)
      const hasSeenInLocalStorage = localStorage.getItem('hasSeenGrammarGuide') === 'true';
      
      if (hasSeenInLocalStorage) {
        setHasSeenGuide(true);
        return;
      }

      // If user is logged in, check Firestore
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.data();

          const hasSeenGrammarGuide = userData?.hasSeenGrammarGuide || false;
          setHasSeenGuide(hasSeenGrammarGuide);
          
          // Sync to localStorage as backup
          if (hasSeenGrammarGuide) {
            localStorage.setItem('hasSeenGrammarGuide', 'true');
          }
        } catch (error) {
          console.error('Error checking guide status:', error);
          // Fallback to localStorage
          setHasSeenGuide(hasSeenInLocalStorage);
        }
      } else {
        setHasSeenGuide(hasSeenInLocalStorage);
      }
    };

    checkGuideStatus();
  }, [user]);


  useEffect(() => {
    const loadSubscription = async () => {
      if (user) {
        try {
          const status = await subscriptionService.getSubscriptionStatus(user.uid);
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Error loading subscription status:', error);
          setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        }
      } else {
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      }
    };
    loadSubscription();
  }, [user]);

  // Auto-advance to pillars view on initial mount when userLevel is provided (e.g., from study plan)
  useEffect(() => {
    if (hasAutoAdvanced.current) return;
    if (userLevel && currentView === 'levels' && selectedLevel === userLevel && !loading && userProgress !== null) {
      console.log('🎯 Auto-advancing grammar to pillars view for level:', userLevel);
      hasAutoAdvanced.current = true;
      setCurrentView('pillars');
    }
  }, [userLevel, currentView, selectedLevel, loading, userProgress]);

  useEffect(() => {
    loadLevelData(selectedLevel);
  }, [user, selectedLevel, i18n.language]);

  const handleGuideComplete = async (dontShowAgain) => {
    // Always save the preference so guide doesn't show again automatically
    // Save to localStorage immediately (works for all users)
    localStorage.setItem('hasSeenGrammarGuide', 'true');
    setHasSeenGuide(true);

    // If user is logged in, also save to Firestore
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          hasSeenGrammarGuide: true,
          grammarGuideCompletedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving guide preference:', error);
        // localStorage already saved, so we're good
      }
    }
    
    setShowGuide(false);
  };

  const handleShowGuide = () => {
    setShowGuide(true);
  };

  const loadLessonPracticeConnections = async (level) => {
    try {
      const levelLower = level.toLowerCase();
      const defaultLang = 'en';
      const langToLoad = i18n.language === 'de' ? 'en' : i18n.language;

      let lessonFileData = null;
      try {
        console.log(`Attempting to load lesson data: /data/grammar-levels/${levelLower}-grammar_${langToLoad}.json`);
        const lessonResponse = await fetch(`/data/grammar-levels/${levelLower}-grammar_${langToLoad}.json`);
        if (!lessonResponse.ok) {
          throw new Error(`Lesson file not found for language: ${langToLoad}`);
        }
        lessonFileData = await lessonResponse.json();
        setLessonData(lessonFileData);
        console.log(`✅ Successfully loaded '${langToLoad}' lesson data for ${level}`);
      } catch (error) {
        console.warn(`Could not load '${langToLoad}' lesson data. Falling back to English.`);
        const fallbackResponse = await fetch(`/data/grammar-levels/${levelLower}-grammar_${defaultLang}.json`);
        if (fallbackResponse.ok) {
          lessonFileData = await fallbackResponse.json();
          setLessonData(lessonFileData);
          console.log(`✅ Successfully loaded fallback '${defaultLang}' lesson data for ${level}`);
        } else {
          console.error(`❌ CRITICAL: Failed to load even the fallback English lesson data for ${level}`);
        }
      }

      let practiceFileData = null;
      try {
        const practiceResponse = await fetch(`/data/grammar/grammar-${levelLower}.json`);
        if (practiceResponse.ok) {
          practiceFileData = await practiceResponse.json();
          setPracticeData(practiceFileData);
        }
      } catch (error) {
        console.log(`No practice data found for ${level}`);
      }

      const connections = createPillarConnections(lessonFileData, practiceFileData);
      setPillarConnections(connections);

    } catch (error) {
      console.error('Error loading lesson-practice connections:', error);
    }
  };

  const createPillarConnections = (lessonData, practiceData) => {
    const connections = {};
    const levelKey = (selectedLevel || '').toUpperCase();

    // Map topic id (e.g. "connectors") -> numeric pillar for this level (practice JSON uses topic ids)
    const topicToPillarNum = {};
    Object.entries(pillarIdToTopicMap).forEach(([key, value]) => {
      if (!levelKey || !key.startsWith(levelKey + '-')) return;
      const num = parseInt(key.split('-')[1], 10);
      if (!Number.isNaN(num)) topicToPillarNum[value] = num;
    });

    const lessonPillars = lessonData?.pillars || [];
    const practiceExercises = practiceData?.exercises || [];
    const practicePillars = {};

    practiceExercises.forEach(exercise => {
      const topicOrPillar = exercise.pillar ?? exercise.topic;
      if (topicOrPillar == null) return;
      const numId = Number(topicOrPillar);
      const finalKey = !Number.isNaN(numId) ? numId : (topicToPillarNum[topicOrPillar] ?? topicOrPillar);
      const numKey = typeof finalKey === 'number' ? finalKey : topicToPillarNum[finalKey];
      const key = numKey != null ? numKey : finalKey;
      if (!practicePillars[key]) {
        practicePillars[key] = { id: key, exerciseCount: 0, exercises: [] };
      }
      practicePillars[key].exerciseCount++;
      practicePillars[key].exercises.push(exercise);
    });

    lessonPillars.forEach(lessonPillar => {
      const lessonPillarId = Number(lessonPillar.id) || lessonPillar.id;
      const uniqueKey = levelKey ? `${levelKey}-${lessonPillarId}` : null;
      const practiceTopicId = uniqueKey ? pillarIdToTopicMap[uniqueKey] : null;

      if (practiceTopicId) {
        connections[lessonPillarId] = {
          id: practiceTopicId,
          lessonId: lessonPillarId,
          pillarNumber: lessonPillarId,
          title: lessonPillar.title || lessonPillar.name,
          description: lessonPillar.description || lessonPillar.subtitle,
          hasLesson: true,
          lessonData: lessonPillar,
          hasPractice: !!practicePillars[lessonPillarId],
          practiceData: practicePillars[lessonPillarId] || null,
          exerciseCount: practicePillars[lessonPillarId]?.exerciseCount || 0,
          isConnected: !!practicePillars[lessonPillarId]
        };
      }
    });

    Object.values(practicePillars).forEach(practicePillar => {
      const key = practicePillar.id;
      if (connections[key]) return;
      const topicTitle = getGrammarTopicTitle(selectedLevel, key);
      connections[key] = {
        id: key,
        lessonId: null,
        pillarNumber: key,
        title: topicTitle || (typeof key === 'string'
          ? key.replace(/_a2/g, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          : String(key)),
        description: t('grammar.connection.lessonComingSoon'),
        hasLesson: false,
        hasPractice: true,
        practiceData: practicePillar,
        exerciseCount: practicePillar.exerciseCount,
        isConnected: false
      };
    });

    return connections;
  };

  const handleLevelSelect = (level) => {
    if (grammarLevels[level].available) {
      setSelectedLevel(level);
      setCurrentView('pillars');
    }
  };

  const handleContinue = () => {
    if (continuationPoint?.pillarId) {
      console.log(`🎯 CONTINUING: Pillar=${continuationPoint.pillarId}, Index=${continuationPoint.exerciseIndex}`);
      setSelectedPillar(continuationPoint.pillarId);
      setInitialExerciseIndex(continuationPoint.exerciseIndex || 0);
      setInitialExerciseId(null);
      setCurrentView('exercises');
    }
  };

  const handleGoToLesson = (pillarIdOrTopicId) => {
    const level = (selectedLevel || userLevel || 'B1').toUpperCase();
    const levelPath = (selectedLevel || userLevel || 'B1').toLowerCase();
    const mapKey = `${level}-${pillarIdOrTopicId}`;
    let topicId = pillarIdToTopicMap[mapKey];
    if (!topicId && typeof pillarIdOrTopicId === 'string') topicId = pillarIdOrTopicId;
    const lessonUrl = topicId ? getGrammarLessonUrl(topicId) : null;
    const examPrefix = examSlug ? `/exams/${examSlug}` : '';
    if (lessonUrl && lessonUrl !== '/practice/grammar') {
      navigate(`${examPrefix}${lessonUrl}`);
    } else {
      navigate(`${examPrefix}/lessons/${levelPath}?pillar=${pillarIdOrTopicId}`);
    }
  };

  const getConnectionStats = () => {
    const connections = Object.values(pillarConnections);
    const connected = connections.filter(c => c.isConnected).length;
    const total = connections.length;
    const practiceOnly = connections.filter(c => !c.hasLesson).length;
    const lessonOnly = connections.filter(c => !c.hasPractice).length;

    return { connected, total, practiceOnly, lessonOnly };
  };

  const stats = getConnectionStats();

  const handleReviewMistake = (mistake) => {
    if (mistake && mistake.pillarId && mistake.exerciseId) {
      setSelectedPillar(mistake.pillarId);
      setInitialExerciseId(mistake.exerciseId);
      setInitialExerciseIndex(0);
      setCurrentView('exercises');
    }
  };

  const handleReviewBookmark = (bookmark) => {
    if (bookmark && bookmark.pillarId && bookmark.exerciseId) {
      setSelectedPillar(bookmark.pillarId);
      setInitialExerciseId(bookmark.exerciseId);
      setInitialExerciseIndex(0);
      setCurrentView('exercises');
    }
  };

  const handleSelectPillar = (pillarId) => {
    let startIndex = 0;

    if (continuationPoint?.pillarId === pillarId && continuationPoint?.exerciseIndex !== undefined) {
      startIndex = continuationPoint.exerciseIndex;
      console.log(`🎯 USING CONTINUATION INDEX: ${startIndex} for pillar ${pillarId}`);
    } else {
      const pillarProgress = userProgress?.pillars?.[pillarId];
      if (pillarProgress) {
        const exercises = Object.values(pillarProgress.exercises || {});
        const completedCount = exercises.filter(e => e.completed).length;
        if (completedCount > 0 && completedCount < (pillarConnections[pillarId]?.exerciseCount || 0)) {
          startIndex = completedCount;
        }
      }
      console.log(`📊 CALCULATED START INDEX: ${startIndex} for pillar ${pillarId}`);
    }

    setSelectedPillar(pillarId);
    setInitialExerciseIndex(startIndex);
    setInitialExerciseId(null);
    setCurrentView('exercises');
    if (setFocusMode) setFocusMode(true);
  };

  // Auto-select pillar from URL params (e.g., when navigating from lesson to practice)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('pillar');
    const pillarId = raw != null && raw !== '' ? (() => {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    })() : null;

    const levelKeyForUrl = (selectedLevel || userLevel || '').toUpperCase();
    const topicIdForPillar = levelKeyForUrl && pillarId != null ? pillarIdToTopicMap[`${levelKeyForUrl}-${pillarId}`] : null;
    const pillarExistsInPractice = pillarId != null && practiceData?.exercises?.some(ex =>
      (ex.pillar != null && Number(ex.pillar) === pillarId) ||
      (ex.topic != null && Number(ex.topic) === pillarId) ||
      (topicIdForPillar != null && (ex.pillar === topicIdForPillar || ex.topic === topicIdForPillar))
    );
    const pillarExistsInConnections = pillarId != null && pillarConnections[pillarId];
    
    console.log(`🔍 URL pillar check: pillarId=${pillarId}, practiceData=${!!practiceData}, loading=${loading}, pillarExists=${pillarExistsInPractice || pillarExistsInConnections}, currentView=${currentView}`);
    
    // Same lock as GrammarPillarSelector: free users only get pillar 1. A link must not get round it,
    // so a locked pillar opens the pillar list instead, where the card shows the lock.
    if (pillarId && !subscriptionStatus) return;
    if (pillarId && subscriptionStatus?.isFree && pillarId !== 1) {
      if (processedPillarFromUrl.current !== pillarId) {
        processedPillarFromUrl.current = pillarId;
        setCurrentView('pillars');
      }
      return;
    }

    // Only auto-select if we have practice data and the pillar exists (either in connections or practice data)
    if (pillarId && practiceData && !loading && (pillarExistsInConnections || pillarExistsInPractice)) {
      // Check if we've already processed this pillar ID to avoid infinite loops
      if (processedPillarFromUrl.current === pillarId && selectedPillar === pillarId && currentView === 'exercises') {
        console.log(`⏭️ Skipping - already on pillar ${pillarId}`);
        return; // Already processed and on the correct view
      }
      
      console.log(`🎯 Auto-selecting pillar from URL: ${pillarId}`);
      processedPillarFromUrl.current = pillarId;
      
      // Get exercise count from practiceData if pillarConnections not ready yet
      const exerciseCount = pillarConnections[pillarId]?.exerciseCount ||
        practiceData.exercises?.filter(ex =>
          Number(ex.pillar) === pillarId || Number(ex.topic) === pillarId ||
          (topicIdForPillar != null && (ex.pillar === topicIdForPillar || ex.topic === topicIdForPillar))
        ).length || 0;
      
      // Call handleSelectPillar logic directly to avoid dependency issues
      let startIndex = 0;
      if (continuationPoint?.pillarId === pillarId && continuationPoint?.exerciseIndex !== undefined) {
        startIndex = continuationPoint.exerciseIndex;
      } else {
        const pillarProgress = userProgress?.pillars?.[pillarId];
        if (pillarProgress) {
          const exercises = Object.values(pillarProgress.exercises || {});
          const completedCount = exercises.filter(e => e.completed).length;
          if (completedCount > 0 && completedCount < exerciseCount) {
            startIndex = completedCount;
          }
        }
      }
      setSelectedPillar(pillarId);
      setInitialExerciseIndex(startIndex);
      setInitialExerciseId(null);
      setCurrentView('exercises');
      if (setFocusMode) setFocusMode(true);
    } else if (!pillarId) {
      // Reset the ref when pillar param is removed
      processedPillarFromUrl.current = null;
    } else if (pillarId && !practiceData) {
      console.log(`⏳ Waiting for practiceData to load for pillar ${pillarId}`);
    } else if (pillarId && !pillarExistsInConnections && !pillarExistsInPractice) {
      console.log(`❌ Pillar ${pillarId} not found in practice data or connections`);
    }
  }, [location.search, practiceData, loading, pillarConnections, continuationPoint, userProgress, setFocusMode, selectedPillar, currentView, subscriptionStatus]);

  const handleBackFromExercises = () => {
    if (setFocusMode) setFocusMode(false);
    setCurrentView('pillars');
    loadLevelData(selectedLevel);
    if (setFocusMode) setFocusMode(false);
    setInitialExerciseId(null);
    setInitialExerciseIndex(0);
    setSelectedPillar(null);
  };

  const handleBackFromPillars = () => {
    setCurrentView('levels');
    loadLevelData(selectedLevel);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Shows on all views */}
      <div className="relative bg-white border-b border-gray-200 px-4 sm:px-6 py-2 sm:py-6">
        {/* Subtle geometric pattern in header */}
        <div className="relative z-10 max-w-7xl mx-auto">
         <div className="flex items-center gap-2 sm:gap-3">
  {/* Left Side: Back Button + Title + Help */}
  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
    <button
      onClick={currentView === 'levels' ? onBack : () => {
        if (currentView === 'exercises') {
          handleBackFromExercises();
        } else if (currentView === 'pillars') {
          handleBackFromPillars();
        }
      }}
      onMouseEnter={() => setIsBackHovered(true)}
      onMouseLeave={() => setIsBackHovered(false)}
      className="hidden sm:inline-flex items-center justify-center p-2 bg-white rounded-lg border border-[#E1E4E8] shadow-[0_4px_20px_rgba(0,0,0,0.05)] relative overflow-hidden transition-all duration-300 hover:shadow-lg flex-shrink-0"
    >
      {/* Wave fill effect (Right-to-Left) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: isBackHovered ? '100%' : '0%',
          background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
          transition: 'width 0.7s ease-out',
          zIndex: 0
        }}
      />
      <ArrowLeft
        className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-500"
        style={{ color: isBackHovered ? '#ffffff' : '#000000' }}
      />
    </button>

      <button

      onClick={handleShowGuide}
      className="hidden sm:flex p-2 bg-white text-black border border-[#E1E4E8] shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:bg-black hover:text-white rounded-lg transition-all duration-300 ease-in-out hover:scale-110 hover:-rotate-6 hover:shadow-lg flex-shrink-0 cursor-pointer items-center justify-center"
    title={t('grammar.guide.help')}
   >
     <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
   </button>

    <div className="flex-1 min-w-0">
      {currentView === 'levels' ? (
        <h1 className="text-sm sm:text-2xl font-space font-black text-black tracking-tight truncate">
          {t('grammar.title')}
        </h1>
      ) : (
        <h1 className="text-sm sm:text-2xl font-space font-black text-black tracking-tight truncate">
          {selectedLevel || userLevel || 'B1'} {t('grammar.lessons', { defaultValue: 'Grammar Lessons' })}
        </h1>
      )}
      <p className="text-[10px] sm:text-sm text-black/60 truncate hidden sm:block">
        {currentView === 'levels' ? t('grammar.subtitle') : ''}
      </p>
    </div>
</div>

            {/* Right Side: Continue Button or Stats */}
            {continuationPoint?.pillarId && currentView === 'levels' ? (
              // Continue Button - Deep Teal Primary Action (desktop only, mobile uses bottom nav)
             <div
  onClick={handleContinue}
  onMouseEnter={() => setIsContinueHovered(true)}
  onMouseLeave={() => setIsContinueHovered(false)}
  className="hidden sm:inline-flex items-center gap-2 sm:gap-4 px-3 py-2 sm:px-4 sm:py-2 bg-[#006D77] rounded-lg sm:rounded-xl border border-[#E1E4E8] shadow-[0_4px_20px_rgba(0,0,0,0.05)] relative overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-xl hover:scale-105 flex-shrink-0"
>
  {/* Wave fill effect - fills left to right on hover */}
  <div
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: isContinueHovered ? '100%' : '0%',
      background: 'linear-gradient(to right, rgba(0,109,119,0.8), rgba(0,109,119,1))',
      transition: 'width 0.7s ease-out',
      zIndex: 0
    }}
  />


           {/* Icon */}
<div
  style={{
    position: 'relative',
    zIndex: 10,
    transition: 'all 0.5s',
    backgroundColor: 'transparent'
  }}
  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
>
  <Zap
    className="w-4 h-4 sm:w-5 sm:h-5"
    style={{
      color: '#ffffff',
      transition: 'color 0.5s'
    }}
  />
</div>


                {/* Text - hidden on mobile */}
               <div className="relative hidden sm:block flex-shrink-0" style={{ zIndex: 10 }}>
    <p
      className="text-xs sm:text-sm font-medium whitespace-nowrap"
      style={{
        color: '#ffffff',
        transition: 'color 0.5s'
      }}
    >
      {selectedLevel} • {pillarConnections[continuationPoint.pillarId]?.title || continuationPoint.pillarId}
    </p>
  </div>

                {/* Arrow */}
                 <span className="relative inline-flex items-center flex-shrink-0" style={{ zIndex: 10 }}>
    <ChevronRight
      className="w-4 h-4 sm:w-5 sm:h-5"
      style={{
        color: '#ffffff',
        transition: 'color 0.5s, transform 0.5s',
        transform: isContinueHovered ? 'translateX(4px)' : 'translateX(0)'
      }}
    />
  </span>
              </div>
            ) : userProgress && currentView !== 'exercises' ? (
              // Stats badges when no continuation, compact on mobile
              <div className="flex items-center gap-1.5 sm:gap-3">
                <div className="text-center px-1.5 sm:px-3 py-1 sm:py-2 bg-white rounded-lg border border-[#E1E4E8] shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:bg-black hover:border-black transition-all duration-300 group flex-shrink-0">
                  <div className="text-xs sm:text-lg font-space font-black text-black group-hover:text-white transition-colors duration-300 leading-tight">
                    {userProgress.stats?.completedExercises || 0}
                  </div>
                  <div className="text-[8px] sm:text-xs text-black/60 font-medium group-hover:text-white/80 transition-colors duration-300 whitespace-nowrap leading-tight">
                    Completed
                  </div>
                </div>

                {userProgress.mistakes?.reviewQueue?.length > 0 && (
                  <div className="text-center px-1.5 sm:px-3 py-1 sm:py-2 bg-white rounded-lg border border-[#E1E4E8] shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:bg-black hover:border-black transition-all duration-300 group flex-shrink-0">
                    <div className="text-xs sm:text-lg font-space font-black text-black group-hover:text-white transition-colors duration-300 leading-tight">
                      {userProgress.mistakes.reviewQueue.length}
                    </div>
                    <div className="text-[8px] sm:text-xs text-black/60 font-medium group-hover:text-white/80 transition-colors duration-300 whitespace-nowrap leading-tight">
                      Mistakes
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {currentView !== 'levels' && (
        <FloatingProgressTracker
          level={selectedLevel}
          currentContext={currentContext}
          grammarData={practiceData}
          onReviewMistakes={handleReviewMistake}
          onReviewBookmark={handleReviewBookmark}
          onSelectPillar={(pillarId) => {
            setSelectedPillar(pillarId);
            setCurrentView('exercises');
          }}
        />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 py-4 sm:p-6 bg-white pb-24 sm:pb-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
          </div>
        ) : (
          <>
            {/* Level Selection View */}
            {currentView === 'levels' && (
              <div className="min-h-screen bg-white relative overflow-hidden">
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-8 pb-24 sm:pb-8">
                  {/* Header */}
                  <div className="text-center mb-4 sm:mb-16">
                    <h1 className="font-space text-2xl sm:text-5xl md:text-6xl font-black text-black mb-2 sm:mb-4 tracking-tight leading-tight sm:leading-none">
                      {t('grammar.levels.title')}
                    </h1>
                  </div>

                  {/* Level Cards Grid */}
                  <div className="grammar-mobile-single-col grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                    {Object.entries(grammarLevels).map(([level, config], index) => (
                      <div
                        key={level}
                        onClick={() => config.available && handleLevelSelect(level)}
                        className={`grammar-level-card rounded-3xl border border-[#E1E4E8] bg-white text-black shadow-[0_4px_20px_rgba(0,0,0,0.05)] ${
                          config.available ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                        }`}
                      >
                        {/* Desktop: Vertical layout */}
                        <div className="hidden sm:block p-8">
                          <div className="grammar-number-bg absolute top-4 right-6 font-space text-[4rem] font-black text-black opacity-15 leading-none">
                            {index + 1}
                          </div>

                          <div className="grammar-decorative-dot grammar-dot-1"></div>
                          <div className="grammar-decorative-dot grammar-dot-2"></div>
                          <div className="grammar-decorative-dot grammar-dot-3"></div>

                          <div className="grammar-shine-effect"></div>

                          {!config.available && (
                            <div className="absolute top-4 right-4 z-10">
                              <Lock className="w-5 h-5 text-gray-400" />
                            </div>
                          )}

                          <div className="grammar-icon-animated relative w-[70px] h-[70px] bg-black rounded-2xl flex items-center justify-center text-white text-3xl mb-6 mx-auto">
                            <GraduationCap className="w-10 h-10" />
                          </div>

                          <h3 className="grammar-title-animated font-space text-4xl font-black text-black text-center mb-2">
                            {level}
                          </h3>

                          <p className="text-sm text-center text-black/60 mb-4 transition-colors duration-500">
                            {t(`grammar.levels.description.${level.toLowerCase()}`)}
                          </p>

                          {config.available && userProgress && level === selectedLevel && (
                            <div className="grammar-progress-info mt-4 pt-4 border-t border-gray-200 transition-all duration-500">
                              <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-gray-600 transition-colors duration-500">
                                  {t('grammar.progress.overall')}
                                </span>
                                <span className="font-medium text-black transition-colors duration-500">
                                  {overallProgress}%
                                </span>
                              </div>
                              <div className="grammar-progress-bar w-full bg-gray-200 rounded-full h-2 transition-all duration-500">
                                <div
                                  className="bg-black h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${overallProgress}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {!config.available && (
                            <div className="mt-4 text-center">
                              <span className="text-sm text-gray-500 font-medium">
                                {t('grammar.levels.comingSoon')}
                              </span>
                            </div>
                          )}

                          <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/20 group-hover:border-white/50 transition-colors"></div>
                          <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/20 group-hover:border-white/50 transition-colors"></div>
                        </div>

      {/* Mobile: Horizontal compact layout */}
      <div className="sm:hidden grammar-mobile-layout">
        {/* Decorative dots for mobile */}
        <div className="grammar-decorative-dot grammar-dot-1"></div>
        <div className="grammar-decorative-dot grammar-dot-2"></div>

        {/* Shine Effect */}
        <div className="grammar-shine-effect"></div>

        {/* Lock Icon */}
        {!config.available && (
          <div className="absolute top-2 right-2 z-10">
            <Lock className="w-4 h-4 text-gray-400" />
          </div>
        )}

        {/* Icon on left */}
        <div className="grammar-icon-animated w-[60px] h-[60px] bg-black rounded-xl flex items-center justify-center text-white flex-shrink-0">
          <GraduationCap className="w-7 h-7" />
        </div>

        {/* Content on right */}
        <div className="grammar-mobile-content flex-1 min-w-0">
          <h3 className="grammar-title-animated font-space text-2xl font-black text-black mb-1">
            {level}
          </h3>
          <p className="text-xs text-black/60 transition-colors duration-500">
            {t(`grammar.levels.description.${level.toLowerCase()}`)}
          </p>

          {/* Show progress badge on mobile if available */}
          {config.available && userProgress && level === selectedLevel && overallProgress > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-black/10 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-black"></div>
              <span className="text-[10px] font-bold text-black">{overallProgress}%</span>
            </div>
          )}
        </div>

        {/* Corner decorations */}
        <div className="absolute top-1 left-1 w-2 h-2 border-t-2 border-l-2 border-black/20"></div>
        <div className="absolute bottom-1 right-1 w-2 h-2 border-b-2 border-r-2 border-black/20"></div>
      </div>
    </div>
  ))}
</div>

      {/* Info Section */}
      <div className="mt-8 sm:mt-16 text-center">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-black/5 rounded-full border border-black/10">
          <Brain className="w-5 h-5 text-black" />
        </div>
      </div>
    </div>
  </div>
)}

            {/* Pillars View */}
            {currentView === 'pillars' && (
              <GrammarPillarSelector
                level={selectedLevel}
                userProgress={userProgress}
                pillarConnections={pillarConnections}
                onSelectPillar={handleSelectPillar}
                onGoToLesson={handleGoToLesson}
                onBack={handleBackFromPillars}
                subscriptionStatus={subscriptionStatus}
                navigate={navigate}
              />
            )}

            {/* Exercises View */}
            {currentView === 'exercises' && selectedPillar && (
              <GrammarExerciseEngine
                level={selectedLevel}
                pillarId={selectedPillar}
                embeddedExercises={pillarConnections[selectedPillar]?.practiceData?.exercises}
                initialExerciseId={initialExerciseId}
                initialExerciseIndex={initialExerciseIndex}
                userProgress={userProgress}
                pillarConnections={pillarConnections}
                onBack={handleBackFromExercises}
                onUpdateProgress={(progress) => {
                  setUserProgress(progress);
                }}
                onContextUpdate={(context) => {
                  setCurrentContext(context);
                }}
                onGoToLesson={() => handleGoToLesson(selectedPillar)}
              />
            )}
          </>
        )}
      </div>
 <GrammarGuide
        isOpen={showGuide}
        onClose={() => {
          setShowGuide(false);
          setHasSeenGuide(true);
          localStorage.setItem('hasSeenGrammarGuide', 'true');
          if (user) {
            updateDoc(doc(db, 'users', user.uid), {
              hasSeenGrammarGuide: true,
              grammarGuideCompletedAt: new Date().toISOString()
            }).catch((err) => console.error('Error saving guide preference:', err));
          }
        }}
        onComplete={handleGuideComplete}
      />

      {/* Bottom Navigation. Levels View */}
      {currentView === 'levels' && (
        <GrammarBottomNav
          buttons={[
            {
              id: 'back',
              icon: ArrowLeft,
              label: t('grammar.back'),
              onClick: onBack
            },
            {
              id: 'home',
              icon: Home,
              label: 'Home',
              onClick: () => navigate('/dashboard')
            },
            continuationPoint?.pillarId ? {
              id: 'continue',
              icon: Play,
              label: t('grammar.exercise.continue', { defaultValue: 'Continue' }),
              onClick: handleContinue,
              active: true,
              color: '#006D77'
            } : null,
            {
              id: 'help',
              icon: HelpCircle,
              label: t('grammar.guide.help', { defaultValue: 'Help' }),
              onClick: handleShowGuide
            }
          ].filter(Boolean)}
        />
      )}

      {/* Bottom Navigation. Pillars View */}
      {currentView === 'pillars' && (
        <GrammarBottomNav
          buttons={[
            {
              id: 'back',
              icon: ArrowLeft,
              label: t('grammar.back'),
              onClick: handleBackFromPillars
            },
            {
              id: 'home',
              icon: Home,
              label: 'Home',
              onClick: () => navigate('/dashboard')
            },
            continuationPoint?.pillarId ? {
              id: 'continue',
              icon: Play,
              label: t('grammar.exercise.continue', { defaultValue: 'Continue' }),
              onClick: handleContinue,
              active: true,
              color: '#006D77'
            } : null,
            {
              id: 'help',
              icon: HelpCircle,
              label: t('grammar.guide.help', { defaultValue: 'Help' }),
              onClick: handleShowGuide
            }
          ].filter(Boolean)}
        />
      )}
    </div>
  );
};

export default GrammarHub;